import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField, MenuItem, Alert, Typography, Chip, FormControlLabel, Checkbox } from "@mui/material";
import { useSelector } from "react-redux";
import { paymentsApi } from "./api";

const AUTHORIZATION_TEXT_VERSION = "v2";
// Legal wording is dynamically composed from the configured Company Name so
// the same code serves any tenant. When companyName is not yet hydrated
// (very early boot) we fall back to the neutral phrase "the school".
const buildAuthorizationText = (companyName: string): string => {
  const who = companyName && companyName.trim() !== "" ? companyName.trim() : "the school";
  return `I authorize ${who} to charge my selected payment method for the outstanding invoice balance on the specified billing day each month, up to the authorized maximum (if any). I understand I can pause or cancel this at any time from the Parent Portal or by contacting the school.`;
};

type Props = {
  open: boolean;
  parentId: string | number;
  studentId: number;
  studentName: string;
  onClose: () => void;
  onSuccess: () => void;
};

const AutoPaySetupDialog: React.FC<Props> = ({ open, parentId, studentId, studentName, onClose, onSuccess }) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [payers, setPayers] = React.useState<any[]>([]);
  const [payerId, setPayerId] = React.useState<number | "">("");
  const [cards, setCards] = React.useState<any[]>([]);
  const companyName = useSelector((s: any) => s?.company?.companyName as string | undefined) ?? "";
  const AUTHORIZATION_TEXT = React.useMemo(() => buildAuthorizationText(companyName), [companyName]);
  const [cardId, setCardId] = React.useState<number | "">("");
  const [billingDay, setBillingDay] = React.useState<number>(1);
  const [effectiveFrom, setEffectiveFrom] = React.useState<string>(new Date().toISOString().slice(0, 10));
  const [authorizedMax, setAuthorizedMax] = React.useState<string>("");
  const [confirmed, setConfirmed] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    (async () => {
      try {
        const p = await paymentsApi.eligiblePayersForStudent(studentId);
        setPayers(p?.data ?? []);
        const primary = (p?.data ?? []).find((x: any) => x.is_primary);
        const currentParent = (p?.data ?? []).find((x: any) => String(x.parent_id) === String(parentId));
        setPayerId(currentParent?.parent_id ?? primary?.parent_id ?? p?.data?.[0]?.parent_id ?? "");
      } catch (e: any) {
        setError(e?.message ?? "Failed to load payers.");
      }
    })();
  }, [open, studentId, parentId]);

  React.useEffect(() => {
    if (!payerId) return;
    (async () => {
      try {
        const r = await paymentsApi.cardsForParent(payerId as number);
        setCards(r?.data ?? []);
        const dflt = (r?.data ?? []).find((c: any) => c.isDefault) ?? r?.data?.[0];
        setCardId(dflt?.id ?? "");
      } catch { setCards([]); setCardId(""); }
    })();
  }, [payerId]);

  const canSubmit = !busy && confirmed && payerId && cardId && billingDay >= 1 && billingDay <= 28;

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await paymentsApi.autoPayCreate(studentId, {
        payer_parent_id: payerId,
        payment_method_id: cardId,
        billing_day: billingDay,
        effective_from: effectiveFrom,
        authorized_maximum: authorizedMax ? Number(authorizedMax) : undefined,
        authorization_text: AUTHORIZATION_TEXT,
        authorization_text_version: AUTHORIZATION_TEXT_VERSION,
      });
      onSuccess();
    } catch (e: any) {
      setError(e?.message ?? "Auto-pay setup failed.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Set Up Auto-Pay · {studentName}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          Auto-pay charges the invoice's outstanding balance on the billing day each month. No charge occurs during setup.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField select fullWidth label="Payer" value={payerId} onChange={(e) => setPayerId(Number(e.target.value))} disabled={busy || payers.length === 0}>
              {payers.map((p) => (
                <MenuItem key={p.parent_id} value={p.parent_id}>
                  {p.first_name} {p.last_name} {p.is_primary && <Chip size="small" color="primary" label="Primary" sx={{ ml: 1 }} />}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField select fullWidth label="Card" value={cardId} onChange={(e) => setCardId(Number(e.target.value))} disabled={busy || cards.length === 0}
              helperText={cards.length === 0 ? "This payer has no saved cards yet." : undefined}>
              {cards.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.cardBrand} •••• {c.cardLast4}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth type="number" label="Billing Day (1–28)" value={billingDay} onChange={(e) => setBillingDay(Number(e.target.value))} disabled={busy} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth type="date" InputLabelProps={{ shrink: true }} label="Effective From" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} disabled={busy} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Authorized Maximum (optional)" value={authorizedMax} onChange={(e) => setAuthorizedMax(e.target.value)} disabled={busy} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ p: 2, background: "rgba(0,0,0,0.03)", borderRadius: 1 }}>
              {AUTHORIZATION_TEXT}
            </Typography>
            <FormControlLabel control={<Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={busy} />} label="I have obtained authorization from the payer." />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit}>{busy ? "Creating…" : "Enable Auto-Pay"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoPaySetupDialog;
